import React, { useEffect, useRef, useState } from "react";
import { Combobox, Input, SimpleSelect } from "@/components/ui";
import { Blog, GetBlogsFilters, PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox, useSocialCoachSession } from "@/utils";

export interface BlogsSearchProps {
  onSearchChange: (searchFilters: GetBlogsFilters) => void;
}

export const BlogsSearch: React.FC<BlogsSearchProps> = ({ onSearchChange }) => {
  const { isPlayer } = useSocialCoachSession();
  const [search, setSearch] = useState<string>();
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState<Blog.Status>();
  const [playerId, setPlayerId] = useState<number>();

  const lastSearchKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const trimmedSearch = searchInput.trim();
      setSearch(trimmedSearch || undefined);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    const searchFilters: GetBlogsFilters = { search, status, playerId };
    const key = JSON.stringify(searchFilters);
    if (lastSearchKeyRef.current === key) return;

    lastSearchKeyRef.current = key;
    onSearchChange(searchFilters);
  }, [onSearchChange, search, status, playerId]);

  return (
    <div className="flex gap-4 items-center flex-wrap flex-auto">
      <Input
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        placeholder="Search"
        className="max-w-80 min-w-50"
      />
      <SimpleSelect
        value={status ?? "ALL"}
        onValueChange={value => setStatus(value === "ALL" ? undefined : (value as Blog.Status))}
        items={[
          { label: "All statuses", value: "ALL" },
          { label: "Published", value: "PUBLISHED" },
          { label: "Draft", value: "DRAFT" },
          { label: "Writing", value: "GENERATING" },
          { label: "Failed", value: "FAILED" },
        ]}
        align="end"
      />
      {!isPlayer && <CoachFilters onPlayerIdChange={setPlayerId} />}
    </div>
  );
};

interface CoachFiltersProps {
  onPlayerIdChange: (playerId: number | undefined) => void;
}

const CoachFilters: React.FC<CoachFiltersProps> = ({ onPlayerIdChange }) => {
  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: player.fullName || player.email || "-",
      })),
    };
  };

  const players = usePaginatedCombobox(getPlayers, false);

  useEffect(() => {
    onPlayerIdChange(players.value?.id);
  }, [players.value]);

  return <Combobox {...players} showClear placeholder="Filter by Player" className="max-w-80 min-w-50" />;
};
